/**
 * Course lesson text is staff-entered HTML (a Writing prompt, a Listening title). The CMS shows it
 * the way learners see it, and it has no HTML sanitizer library, so this keeps a short list of
 * formatting tags and a few style properties and drops everything else before a lesson is put on
 * the page (UD-618). It follows the website's own list in `src/ultis/courseHtml.ts`, with the
 * extras the CMS rich text editor can produce (colors, highlight, links, sub and superscript).
 */

const ALLOWED_TAGS = new Set([
	'p',
	'br',
	'strong',
	'b',
	'em',
	'i',
	'u',
	's',
	'strike',
	'span',
	'div',
	'ul',
	'ol',
	'li',
	'h1',
	'h2',
	'h3',
	'h4',
	'h5',
	'h6',
	'blockquote',
	'mark',
	'sub',
	'sup',
	'code',
	'pre',
	'hr',
	'a',
	'img',
])

// A tag of this kind is dropped together with what is inside it.
const DROPPED_WITH_CONTENT = new Set([
	'script',
	'style',
	'iframe',
	'object',
	'embed',
	'noscript',
	'template',
	'textarea',
	'select',
	'svg',
	'math',
])

const BLOCK_TAGS = new Set([
	'p',
	'div',
	'ul',
	'ol',
	'li',
	'h1',
	'h2',
	'h3',
	'h4',
	'h5',
	'h6',
	'blockquote',
	'pre',
	'hr',
])

const ALLOWED_STYLE_PROPERTIES = new Set([
	'color',
	'background-color',
	'font-weight',
	'font-style',
	'text-decoration',
	'text-align',
])

// Colors and plain keywords only: no url(), no expression(), no var().
const SAFE_STYLE_VALUE = /^(#[0-9a-f]{3,8}|rgba?\(\s*[\d.]+%?(\s*,\s*[\d.]+%?){2,3}\s*\)|[a-z]+(\s+[a-z]+)?|\d{3})$/i

const HTML_TAG = /<\/?[a-z][a-z0-9-]*(\s[^<>]*)?\/?>/i

/** True when the text carries HTML tags (a plain text prompt does not). */
export const looksLikeHtml = (value?: string | null) =>
	HTML_TAG.test(String(value || ''))

const escapeHtml = (text: string) =>
	text
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')

const cleanStyle = (style: string) =>
	style
		.split(';')
		.map((declaration) => {
			const index = declaration.indexOf(':')
			if (index < 0) return ''
			const property = declaration.slice(0, index).trim().toLowerCase()
			const value = declaration.slice(index + 1).trim()
			if (!ALLOWED_STYLE_PROPERTIES.has(property)) return ''
			if (!SAFE_STYLE_VALUE.test(value)) return ''
			return `${property}: ${value}`
		})
		.filter(Boolean)
		.join('; ')

const isWebUrl = (value: string) => /^https?:\/\//i.test(value.trim())

const cleanNode = (node: Node, doc: Document): Node | null => {
	if (node.nodeType === Node.TEXT_NODE) {
		return doc.createTextNode(node.textContent || '')
	}
	if (node.nodeType !== Node.ELEMENT_NODE) return null
	const element = node as Element
	const tag = element.tagName.toLowerCase()
	if (DROPPED_WITH_CONTENT.has(tag)) return null

	const children = Array.from(element.childNodes)
		.map((child) => cleanNode(child, doc))
		.filter(Boolean) as Node[]

	if (!ALLOWED_TAGS.has(tag)) {
		// An unknown tag keeps its text and loses the tag itself.
		const fragment = doc.createDocumentFragment()
		children.forEach((child) => fragment.appendChild(child))
		return fragment
	}

	const clean = doc.createElement(tag)
	const style = cleanStyle(element.getAttribute('style') || '')
	if (style) clean.setAttribute('style', style)
	if (tag === 'a') {
		const href = element.getAttribute('href') || ''
		if (isWebUrl(href)) {
			clean.setAttribute('href', href.trim())
			clean.setAttribute('target', '_blank')
			clean.setAttribute('rel', 'noopener noreferrer')
		}
	}
	if (tag === 'img') {
		const src = element.getAttribute('src') || ''
		if (!isWebUrl(src)) return null
		clean.setAttribute('src', src.trim())
		clean.setAttribute('alt', element.getAttribute('alt') || '')
	}
	children.forEach((child) => clean.appendChild(child))
	return clean
}

const parseFragment = (html: string) =>
	new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html').body

/**
 * A lesson as safe HTML: formatting tags only, no scripts, no event attributes, no javascript:
 * links, and only a few harmless style properties. Plain text keeps its line breaks.
 */
export const sanitizeLessonHtml = (html?: string | null) => {
	const source = String(html || '')
	if (!looksLikeHtml(source)) {
		return escapeHtml(source).replace(/\r?\n/g, '<br />')
	}
	if (typeof DOMParser === 'undefined') return escapeHtml(source)
	const body = parseFragment(source)
	const doc = body.ownerDocument
	const holder = doc.createElement('div')
	Array.from(body.childNodes).forEach((child) => {
		const clean = cleanNode(child, doc)
		if (clean) holder.appendChild(clean)
	})
	return holder.innerHTML
}

const collectText = (node: Node, parts: string[]) => {
	if (node.nodeType === Node.TEXT_NODE) {
		parts.push(node.textContent || '')
		return
	}
	if (node.nodeType !== Node.ELEMENT_NODE) return
	const element = node as Element
	const tag = element.tagName.toLowerCase()
	if (DROPPED_WITH_CONTENT.has(tag)) return
	if (tag === 'br') {
		parts.push('\n')
		return
	}
	element.childNodes.forEach((child) => collectText(child, parts))
	if (BLOCK_TAGS.has(tag)) parts.push('\n')
}

/**
 * A lesson as plain text, for the one or two lines a list shows. Blocks and line breaks become
 * line breaks, so "<p>Topic: A</p><p>Tell us</p>" reads "Topic: A\nTell us", not "Topic: ATell us".
 */
export const lessonHtmlToText = (html?: string | null) => {
	const source = String(html || '')
	if (!looksLikeHtml(source) || typeof DOMParser === 'undefined') return source
	const parts: string[] = []
	parseFragment(source).childNodes.forEach((child) => collectText(child, parts))
	return parts
		.join('')
		.replace(/ /g, ' ')
		.replace(/[ \t]+\n/g, '\n')
		.replace(/\n{3,}/g, '\n\n')
		.trim()
}
